import React, { useEffect, useRef, useState, useMemo } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import '../styles/QuantumView.css';

const QuantumView = ({ moleculeData }) => {
  const mountRef = useRef(null);
  const containerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Calculate quantum properties based on molecular structure
  const calculateQuantumData = useMemo(() => {
    if (!moleculeData || !moleculeData.atoms || !moleculeData.bonds) {
      return {
        levels: [-10.5, -9.2, -8.1, -7.3, -6.2, -5.1, -4.3, -2.0, -0.7, 1.4, 3.6, 5.8],
        occupied: 7,
        homo: 6,
        lumo: 7,
        gap: 4.5,
        description: "This molecule has an estimated HOMO-LUMO gap of 4.5 eV. A larger gap typically indicates higher stability and lower reactivity.",
        formula: "",
        electronCount: 0
      };
    }
    
    // Count atoms by element
    const elementCounts = {};
    moleculeData.atoms.forEach(atom => {
      if (atom.element) {
        elementCounts[atom.element] = (elementCounts[atom.element] || 0) + 1;
      }
    });
    
    // Calculate formula
    let formula = '';
    const sortedElements = ['C', 'H', 'N', 'O', 'P', 'S', 'F', 'Cl', 'Br', 'I'];
    
    for (const element of sortedElements) {
      if (elementCounts[element]) {
        formula += element;
        if (elementCounts[element] > 1) {
          formula += elementCounts[element];
        }
      }
    }
    
    // Calculate exact mass and electron count
    const elementMasses = {
      'H': 1.008, 'C': 12.011, 'N': 14.007, 'O': 15.999, 'F': 18.998,
      'P': 30.974, 'S': 32.065, 'Cl': 35.453, 'Br': 79.904, 'I': 126.904
    };
    
    const atomicNumbers = {
      'H': 1, 'C': 6, 'N': 7, 'O': 8, 'F': 9, 'P': 15, 'S': 16, 'Cl': 17, 'Br': 35, 'I': 53
    };
    
    let exactMass = 0;
    let totalElectrons = 0;
    
    for (const [element, count] of Object.entries(elementCounts)) {
      exactMass += (elementMasses[element] || 0) * count;
      totalElectrons += (atomicNumbers[element] || 6) * count;
    }
    
    // Detect specific molecules
    const name = moleculeData.name || "";
    const isCaffeine = name.toLowerCase().includes('caffeine');
    const isAspirin = name.toLowerCase().includes('aspirin');
    const isBenzene = name.toLowerCase().includes('benzene');
    const hasAromaticRing = moleculeData.bonds && moleculeData.bonds.some(bond => 
      bond.type === 'aromatic' || bond.order === 1.5
    );
    
    // Determine gap based on molecular structure
    let gap;
    if (isCaffeine) {
      gap = 3.1;
    } else if (isAspirin) {
      gap = 2.7;
    } else if (isBenzene) {
      gap = 2.7;
    } else if (hasAromaticRing) {
      gap = 2.9;
    } else if (elementCounts['O'] > 0 || elementCounts['N'] > 0) {
      gap = 4.2;
    } else {
      gap = 4.5;
    }
    
    // Calculate occupied orbitals based on valence electrons
    const valenceMap = {
      'H': 1, 'C': 4, 'N': 5, 'O': 6, 'F': 7, 'P': 5, 'S': 6, 'Cl': 7, 'Br': 7, 'I': 7
    };
    
    let totalValence = 0;
    for (const [element, count] of Object.entries(elementCounts)) {
      totalValence += (valenceMap[element] || 4) * count;
    }
    
    const bondCount = moleculeData.bonds ? moleculeData.bonds.length : 0;
    const adjustedValence = totalValence - bondCount;
    const occupied = Math.ceil(adjustedValence / 2);
    
    // Generate energy levels
    const levels = [];
    const lowerLevels = occupied;
    const upperLevels = 5;
    
    for (let i = 0; i < lowerLevels; i++) {
      const energy = -12.0 + (i * (10.0 / Math.max(1, lowerLevels)));
      levels.push(energy);
    }
    
    const homoEnergy = levels[levels.length - 1];
    const lumoEnergy = homoEnergy + gap;
    
    for (let i = 0; i < upperLevels; i++) {
      const energy = lumoEnergy + (i * 1.5);
      levels.push(energy);
    }
    
    // Generate description
    let description;
    
    if (isCaffeine) {
      description = "Caffeine has a moderate HOMO-LUMO gap typical of bioactive compounds, influencing its stimulant properties.";
    } else if (isAspirin) {
      description = "Aspirin's HOMO-LUMO gap reflects its aromatic stability while maintaining reactivity at the acetyl group.";
    } else if (isBenzene) {
      description = "Benzene's delocalized π-electron system creates a moderate HOMO-LUMO gap, giving it aromatic stability.";
    } else if (hasAromaticRing) {
      description = `This molecule contains aromatic rings with a HOMO-LUMO gap of ${gap.toFixed(1)} eV, giving it moderate stability and reactivity.`;
    } else {
      description = `This molecule has an estimated HOMO-LUMO gap of ${gap.toFixed(1)} eV. A larger gap typically indicates higher stability and lower reactivity.`;
    }
    
    return {
      levels: levels,
      occupied: lowerLevels,
      homo: lowerLevels - 1,
      lumo: lowerLevels,
      gap: gap,
      description: description,
      formula: formula,
      electronCount: totalElectrons,
      exactMass: exactMass.toFixed(4)
    };
  }, [moleculeData]);

  // Check for mobile on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!mountRef.current) return;
    
    setIsLoading(true);
    
    // Get fixed dimensions
    const width = mountRef.current.clientWidth || 500;
    const height = mountRef.current.clientHeight || 300;
    
    // Create scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f5f5);
    
    // Create camera
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    camera.position.z = 30;
    
    // Create renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    
    // Clear and append
    mountRef.current.innerHTML = '';
    mountRef.current.appendChild(renderer.domElement);
    
    // Add lighting
    const light = new THREE.AmbientLight(0xffffff, 0.8);
    scene.add(light);
    
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.6);
    dirLight.position.set(5, 10, 7);
    scene.add(dirLight);
    
    // Add controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    
    // Create a main group
    const group = new THREE.Group();
    
    // Energy level data
    const { levels, occupied, homo, lumo } = calculateQuantumData;
    const spacing = 2;
    const levelHeight = 0.5;
    const barWidth = 12;
    
    // Add background panel
    const panelGeometry = new THREE.PlaneGeometry(barWidth + 6, 30);
    const panelMaterial = new THREE.MeshBasicMaterial({
      color: 0xf5f5f5, // Match background color
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.8
    });
    const panel = new THREE.Mesh(panelGeometry, panelMaterial);
    panel.position.z = -0.5;
    group.add(panel);
    
    // Create orbital bars
    levels.forEach((energy, i) => {
      const posY = energy * spacing;
      const isHOMO = i === homo;
      const isLUMO = i === lumo;
      const isOccupied = i < occupied;
      
      // Color based on orbital type
      const barColor = isHOMO ? 0x3498db : isLUMO ? 0xe74c3c : isOccupied ? 0x2ecc71 : 0xaaaaaa;
      
      // Create bar
      const bar = new THREE.Mesh(
        new THREE.BoxGeometry(barWidth, levelHeight, levelHeight),
        new THREE.MeshPhongMaterial({
          color: barColor,
          specular: 0xffffff,
          shininess: 70
        })
      );
      
      bar.position.y = posY;
      group.add(bar);
      
      // Add electrons for occupied orbitals
      if (isOccupied) {
        const electronCount = (i < homo) ? 2 : 1;
        
        for (let e = 0; e < electronCount; e++) {
          const offset = electronCount === 2 ? (e * 2 - 1) : 0;
          const electron = new THREE.Mesh(
            new THREE.SphereGeometry(0.3, 16, 16),
            new THREE.MeshPhongMaterial({
              color: 0xff5252,
              emissive: 0xbb3333
            })
          );
          
          electron.position.set(barWidth / 2 + 1, posY, offset * 0.6);
          group.add(electron);
        }
      }
    });
    
    // Add HOMO-LUMO gap visualization
    const homoY = levels[homo] * spacing;
    const lumoY = levels[lumo] * spacing;
    
    // Create gap visualization if values are valid
    if (!isNaN(homoY) && !isNaN(lumoY)) {
      const gapHeight = Math.abs(lumoY - homoY) - levelHeight;
      
      if (gapHeight > 0) {
        const gapMesh = new THREE.Mesh(
          new THREE.BoxGeometry(barWidth * 0.5, gapHeight, levelHeight * 0.3),
          new THREE.MeshPhongMaterial({
            color: 0xf39c12,
            transparent: true,
            opacity: 0.7,
            specular: 0xffffff,
            shininess: 90
          })
        );
        gapMesh.position.x = barWidth * 0.2;
        gapMesh.position.y = (homoY + lumoY) / 2;
        gapMesh.position.z = levelHeight;
        group.add(gapMesh);
      }
    }
    
    // Add the group to the scene
    scene.add(group);
    
// Center the group in the scene automatically
const box = new THREE.Box3().setFromObject(group);
const center = new THREE.Vector3();
box.getCenter(center);
group.position.sub(center);
group.position.z = 0;

    
    // Slightly rotate for better 3D effect
    group.rotation.y = 0.2;
    
    // Animation loop
    let animationFrameId;
    
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    
    animate();
    setIsLoading(false);
    
    // Handle window resize
    const handleResize = () => {
      if (!mountRef.current) return;
      
      const width = mountRef.current.clientWidth;
      const height = mountRef.current.clientHeight;
      
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Cleanup function
    return () => {
      window.removeEventListener('resize', handleResize);
      
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      
      if (mountRef.current && renderer.domElement) {
        if (mountRef.current.contains(renderer.domElement)) {
          mountRef.current.removeChild(renderer.domElement);
        }
      }
      
      renderer.dispose();
      scene.clear();
    };
  }, [calculateQuantumData]);

  const currentProperties = [
    { icon: '☕', label: 'Molecule', value: moleculeData?.name || "Unknown" },
    { icon: '🧪', label: 'Formula', value: calculateQuantumData.formula || "Unknown" },
    { icon: '⚛️', label: 'Electrons (approx)', value: calculateQuantumData.electronCount || "0" },
    { icon: '⚡', label: 'HOMO-LUMO gap', value: `${calculateQuantumData.gap.toFixed(1)} eV` }
  ];

  return (
    <div className="quantum-viewer-container" ref={containerRef} style={{ 
      padding: '15px',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      height: '100%',
      width: '100%',
      gap: '15px'
    }}>
      {/* First column - Summary */}
      <div className="quantum-summary" style={{ 
        flex: isMobile ? 'none' : '1',
        width: isMobile ? '100%' : 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '15px' 
      }}>
        <div style={{
          backgroundColor: 'white',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          borderRadius: '6px',
          padding: '15px'
        }}>
          <h3 style={{ marginTop: 0, fontSize: '16px' }}>Quantum Properties</h3>
          
          <div style={{ 
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
              {moleculeData?.name || "No molecule selected"}
            </div>
            
            <div style={{ 
              backgroundColor: '#e74c3c', 
              padding: '3px 8px', 
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: 'bold',
              color: 'white'
            }}>
              Gap: {calculateQuantumData.gap.toFixed(1)} eV
            </div>
          </div>
          
          <div>
            {currentProperties.map((prop, index) => (
              <div key={index} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '5px',
                marginBottom: '8px'
              }}>
                <span style={{ fontSize: '16px' }}>{prop.icon}</span>
                <span style={{ fontWeight: '500', color: '#555' }}>{prop.label}:</span>
                <span style={{ color: '#222' }}>{prop.value}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div style={{
          backgroundColor: 'white',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          borderRadius: '6px',
          padding: '15px'
        }}>
          <h3 style={{ marginTop: 0, fontSize: '16px' }}>Description</h3>
          
          <p style={{ 
            margin: 0,
            lineHeight: '1.5',
            fontSize: '14px'
          }}>
            {calculateQuantumData.description}
          </p>
        </div>
      </div>
      
      {/* Second column - Visualization */}
      <div className="quantum-visualization" style={{ 
  flex: isMobile ? 'none' : '2',
  width: isMobile ? '100%' : 'auto',
  height: isMobile ? '300px' : '100%', // Let it fill parent height on desktop
  position: 'relative',
  backgroundColor: '#f5f5f5',
  borderRadius: '0',
  boxShadow: 'none',
  marginBottom: isMobile ? '15px' : '0',
  overflow: 'hidden',
  display: 'flex',                // Important: Let child fill height
  alignItems: 'center',          // Vertically center child
  justifyContent: 'center'       // Horizontally center child (if needed)
}}>

        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 10,
          pointerEvents: 'none'
        }}>
          <span style={{
            display: 'inline-block',
            backgroundColor: 'rgba(255,255,255,0.8)',
            padding: '5px 10px',
            borderRadius: '4px',
            fontSize: '14px',
            fontWeight: '500',
            color: '#333'
          }}>
            Quantum Orbital Analysis
          </span>
        </div>
        
        <div ref={mountRef} style={{ 
  width: '100%', 
  height: '100%', 
  paddingTop: '0px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}}></div>

        
        {isLoading && (
          <div style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'rgba(255,255,255,0.7)',
            zIndex: 5
          }}>
            <p>Loading...</p>
          </div>
        )}
      </div>
      
      {/* Third column - Details & Legend */}
      <div className="quantum-details" style={{ 
        flex: isMobile ? 'none' : '1',
        width: isMobile ? '100%' : 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '15px'
      }}>
        <div style={{
          backgroundColor: 'white',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          borderRadius: '6px',
          padding: '15px'
        }}>
          <h3 style={{ marginTop: 0, fontSize: '16px' }}>Orbital Legend</h3>
          
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '10px',
            fontSize: '13px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ 
                display: 'inline-block', 
                width: '16px', 
                height: '16px', 
                backgroundColor: '#3498db',
                marginRight: '8px',
                borderRadius: '3px'
              }}></span>
              <span>HOMO (Highest Occupied Molecular Orbital)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ 
                display: 'inline-block', 
                width: '16px', 
                height: '16px', 
                backgroundColor: '#e74c3c',
                marginRight: '8px',
                borderRadius: '3px' 
              }}></span>
              <span>LUMO (Lowest Unoccupied Molecular Orbital)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ 
                display: 'inline-block', 
                width: '16px', 
                height: '16px', 
                backgroundColor: '#2ecc71',
                marginRight: '8px',
                borderRadius: '3px' 
              }}></span>
              <span>Occupied Orbitals (filled with electrons)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ 
                display: 'inline-block', 
                width: '16px', 
                height: '16px', 
                backgroundColor: '#aaaaaa',
                marginRight: '8px',
                borderRadius: '3px' 
              }}></span>
              <span>Unoccupied Orbitals (empty)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ 
                display: 'inline-block', 
                width: '16px', 
                height: '16px', 
                backgroundColor: '#f39c12',
                marginRight: '8px',
                borderRadius: '3px' 
              }}></span>
              <span>Energy Gap (energy required for excitation)</span>
            </div>
          </div>
        </div>
        
        <div style={{
          backgroundColor: 'white',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          borderRadius: '6px',
          padding: '15px'
        }}>
          <h3 style={{ marginTop: 0, fontSize: '16px' }}>What am I looking at?</h3>

<p style={{ 
  margin: 0,
  lineHeight: '1.5',
  fontSize: '13px'
}}>
  Think of this as a ladder for electrons. Green steps are already occupied. Blue is the highest step someone’s on (HOMO), red is the next empty step (LUMO), and the orange gap is the energy it takes to make the jump. A big gap means the molecule is calm and stable. A small gap means it's reactive.
</p>

        </div>
      </div>
    </div>
  );
};

export default QuantumView;